import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Shield, Plus, Edit, Trash2, Users, Lock, CheckCircle, XCircle, Search
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
export default function RoleManagement() {
  const { user } = useAuth();
  const [roles, setRoles] = useState({ system_roles: [], custom_roles: [] });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    permissions: [],
    hidden_modules: []
  });
  const [modules, setModules] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [rolesRes, permsRes, modulesRes] = await Promise.all([
        fetch(`${API_URL}/api/roles`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/permissions`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/role-modules`, { headers, credentials: 'include' })
      ]);

      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (permsRes.ok) setPermissions(await permsRes.json());
      if (modulesRes.ok) setModules(await modulesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const res = await fetch(`${API_URL}/api/users`, { headers, credentials: 'include' });
      if (res.ok) setAllUsers(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleOpenAssign = (role) => {
    setAssignDialog(role);
    setUserSearch('');
    fetchAllUsers();
  };

  const handleAssignRole = async (userId, roleCode) => {
    setAssignLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ role: roleCode }),
        credentials: 'include'
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Role assigned successfully');
      fetchAllUsers();
    } catch (e) { toast.error(e.message); }
    finally { setAssignLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hrms-token');
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      let url, method, body;
      if (editingRole?.is_system_role) {
        url = `${API_URL}/api/roles/builtin/${editingRole.code}`;
        method = 'PUT';
        body = { permissions: formData.permissions, hidden_modules: formData.hidden_modules };
      } else if (editingRole) {
        url = `${API_URL}/api/roles/${editingRole.role_id}`;
        method = 'PUT';
        body = formData;
      } else {
        url = `${API_URL}/api/roles`;
        method = 'POST';
        body = formData;
      }

      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(body),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(editingRole ? 'Role updated' : 'Role created');
        setIsDialogOpen(false);
        setEditingRole(null);
        setFormData({ name: '', code: '', description: '', permissions: [], hidden_modules: [] });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save role');
      }
    } catch (error) {
      toast.error('Failed to save role');
    }
  };

  const handleReset = async (role) => {
    if (!confirm(`Reset "${role.name}" back to its default permissions and show all its modules again?`)) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/roles/builtin/${role.code}/reset`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`${role.name} reset to defaults`);
        setIsDialogOpen(false);
        setEditingRole(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reset role');
      }
    } catch (e) {
      toast.error('Failed to reset role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permissions: role.permissions || [],
      hidden_modules: role.hidden_modules || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/roles/${roleId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Role deleted');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const togglePermission = (permCode) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter(p => p !== permCode)
        : [...prev.permissions, permCode]
    }));
  };

  const toggleModuleHidden = (moduleKey) => {
    setFormData(prev => ({
      ...prev,
      hidden_modules: prev.hidden_modules.includes(moduleKey)
        ? prev.hidden_modules.filter(m => m !== moduleKey)
        : [...prev.hidden_modules, moduleKey]
    }));
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.category_code;
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});

  const categoryNames = {
    users: 'User Management',
    roles: 'Role Management',
    entities: 'Entity Management',
    employees: 'Employee Management',
    attendance: 'Attendance',
    leaves: 'Leave Management',
    payroll: 'Payroll',
    reports: 'Reports',
    settings: 'Settings'
  };

  const allRoles = [...roles.system_roles, ...roles.custom_roles];
  const filteredRoles = allRoles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="role-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Role Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Define roles and assign permissions to control access
          </p>
        </div>
        {isSuperAdmin && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingRole(null);
              setFormData({ name: '', code: '', description: '', permissions: [], hidden_modules: [] });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingRole?.is_system_role ? `Edit Built-in Role — ${editingRole.name}`
                    : editingRole ? 'Edit Role' : 'Create New Role'}
                </DialogTitle>
                <DialogDescription>
                  {editingRole?.is_system_role
                    ? 'Adjust this role\u2019s permissions and hide modules. You can reset to defaults anytime.'
                    : 'Define role permissions for granular access control'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Role Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Department Coordinator"
                      required
                      disabled={!!editingRole?.is_system_role}
                      data-testid="role-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Role Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="e.g., dept_coordinator"
                      required
                      disabled={!!editingRole}
                      data-testid="role-code-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the role"
                    disabled={!!editingRole?.is_system_role}
                    data-testid="role-description-input"
                  />
                </div>

                {/* Module visibility */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-lg font-semibold">Modules this role can see</Label>
                    <p className="text-sm text-slate-500">Turn a module off to hide it from the menu and block its page for this role.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {modules.map(mod => {
                      const visible = !formData.hidden_modules.includes(mod.key);
                      return (
                        <label
                          key={mod.key}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${visible ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30' : 'bg-slate-50 border-slate-200 opacity-70 dark:bg-slate-800'}`}
                          data-testid={`module-toggle-${mod.key}`}
                        >
                          <span className="text-sm font-medium">{mod.label}</span>
                          <span className="flex items-center gap-1.5 text-xs">
                            {visible
                              ? <><CheckCircle className="w-4 h-4 text-emerald-600" /> Visible</>
                              : <><XCircle className="w-4 h-4 text-slate-400" /> Hidden</>}
                            <Checkbox
                              checked={visible}
                              onCheckedChange={() => toggleModuleHidden(mod.key)}
                              data-testid={`module-checkbox-${mod.key}`}
                            />
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Permissions */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Permissions</Label>
                  <Accordion type="multiple" className="w-full">
                    {Object.entries(groupedPermissions).map(([category, perms]) => (
                      <AccordionItem key={category} value={category}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            <span>{categoryNames[category] || category}</span>
                            <Badge variant="outline" className="ml-2">
                              {perms.filter(p => formData.permissions.includes(p.code)).length}/{perms.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                            {perms.map(perm => (
                              <div key={perm.code} className="flex items-center space-x-2">
                                <Checkbox
                                  id={perm.code}
                                  checked={formData.permissions.includes(perm.code)}
                                  onCheckedChange={() => togglePermission(perm.code)}
                                  data-testid={`perm-${perm.code}`}
                                />
                                <Label htmlFor={perm.code} className="text-sm cursor-pointer">
                                  {perm.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="flex items-center gap-3">
                    <p className="text-sm text-slate-500">
                      {formData.permissions.length} permissions · {modules.length - formData.hidden_modules.length}/{modules.length} modules visible
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {editingRole?.is_system_role && (
                      <Button type="button" variant="outline" className="text-amber-600"
                        onClick={() => handleReset(editingRole)} data-testid="reset-role-btn">
                        Reset to Default
                      </Button>
                    )}
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-primary" data-testid="save-role-btn">
                      {editingRole ? 'Save Changes' : 'Create Role'}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search roles..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="role-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Roles Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Roles ({allRoles.length})</TabsTrigger>
          <TabsTrigger value="system">System Roles ({roles.system_roles.length})</TabsTrigger>
          <TabsTrigger value="custom">Custom Roles ({roles.custom_roles.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <RoleTable 
            roles={filteredRoles} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            onAssign={handleOpenAssign}
            isSuperAdmin={isSuperAdmin}
            loading={loading}
          />
        </TabsContent>
        <TabsContent value="system">
          <RoleTable 
            roles={roles.system_roles.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            onAssign={handleOpenAssign}
            isSuperAdmin={isSuperAdmin}
            loading={loading}
          />
        </TabsContent>
        <TabsContent value="custom">
          <RoleTable 
            roles={roles.custom_roles.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            onAssign={handleOpenAssign}
            isSuperAdmin={isSuperAdmin}
            loading={loading}
          />
        </TabsContent>
      </Tabs>

      {/* Assign Role Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={v => { if (!v) setAssignDialog(null); }}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> Assign Users to "{assignDialog?.name}"
            </DialogTitle>
            <DialogDescription>
              Change a user's role to <Badge variant="outline">{assignDialog?.code}</Badge>. Users currently with this role are highlighted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search users by name or email..." className="pl-10" value={userSearch}
                onChange={e => setUserSearch(e.target.value)} data-testid="assign-user-search" />
            </div>
            <div className="border rounded-sm max-h-[50vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">User</TableHead>
                    <TableHead className="text-xs uppercase">Current Role</TableHead>
                    <TableHead className="text-xs uppercase w-28">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers
                    .filter(u => {
                      if (!userSearch) return true;
                      const q = userSearch.toLowerCase();
                      return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
                    })
                    .map(u => {
                      const isCurrentRole = u.role === assignDialog?.code;
                      return (
                        <TableRow key={u.user_id} className={isCurrentRole ? 'bg-green-50' : ''} data-testid={`assign-row-${u.user_id}`}>
                          <TableCell>
                            <p className="text-sm font-medium">{u.name || u.email}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant={isCurrentRole ? 'default' : 'outline'} className="text-xs">
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isCurrentRole ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Assigned</Badge>
                            ) : (
                              <Button size="sm" variant="outline"
                                disabled={assignLoading}
                                onClick={() => handleAssignRole(u.user_id, assignDialog?.code)}
                                data-testid={`assign-btn-${u.user_id}`}>
                                <Shield className="w-3 h-3 mr-1" /> Assign
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleTable({ roles, onEdit, onDelete, onAssign, isSuperAdmin, loading }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (roles.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">No roles found</h3>
          <p className="text-slate-500">Create a custom role to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map(role => (
              <TableRow key={role.role_id || role.code} data-testid={`role-row-${role.code}`}>
                <TableCell>
                  <div>
                    <p className="font-medium">{role.name}</p>
                    <p className="text-sm text-slate-500 truncate max-w-[200px]">
                      {role.description || 'No description'}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                    {role.code}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge variant={role.is_system_role ? 'default' : 'outline'}>
                    {role.is_system_role ? 'System' : 'Custom'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className="w-fit">
                      {role.permissions?.length || 0} permissions
                    </Badge>
                    {(role.hidden_modules?.length || 0) > 0 && (
                      <Badge variant="outline" className="w-fit text-amber-600 border-amber-300" data-testid={`role-hidden-count-${role.code}`}>
                        {role.hidden_modules.length} module{role.hidden_modules.length > 1 ? 's' : ''} hidden
                      </Badge>
                    )}
                    {role.is_customized && (
                      <Badge variant="outline" className="w-fit text-blue-600 border-blue-300">Customized</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={role.is_active !== false ? 'badge-success' : 'badge-error'}>
                    {role.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {isSuperAdmin && role.code !== 'super_admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(role)}
                        data-testid={`edit-role-${role.code}`}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    )}
                    {isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAssign(role)}
                        title="Assign users to this role"
                        data-testid={`assign-role-${role.code}`}
                      >
                        <Users className="w-4 h-4 mr-1" /> Assign
                      </Button>
                    )}
                    {!role.is_system_role && isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => onDelete(role.role_id)}
                        data-testid={`delete-role-${role.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
